'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

type PostStatus = 'published' | 'draft' | 'scheduled' | 'archived';
type PostPlatform = 'instagram' | 'tiktok' | 'facebook' | 'linkedin';

interface BrandPost {
  id: string;
  brand: string;
  brandLogo: string;
  brandLogoAlt: string;
  title: string;
  content: string;
  platform: PostPlatform[];
  status: PostStatus;
  scheduledDate?: string;
  publishedDate?: string;
  image?: string;
  imageAlt?: string;
  reach?: string;
  engagement?: string;
  clicks?: string;
  category: string;
}

const mockPosts: BrandPost[] = [
{
  id: 'post-0',
  brand: 'Institut Reversal Group',
  brandLogo: '/assets/images/app_logo.png',
  brandLogoAlt: 'Institut Reversal Group logo',
  title: 'FORMATION DE PROFESSIONNALISATION - CRÉATEURS DE CONTENU & INFLUENCEURS BY INSTITUT REVERSAL GROUP',
  content: 'Formation de professionnalisation dédiée aux créateurs de contenu et influenceurs. Développez vos compétences, maîtrisez votre personal branding et boostez votre carrière digitale avec Institut Reversal Group.',
  platform: ['instagram', 'tiktok', 'facebook'],
  status: 'published',
  publishedDate: '18 Aoû 2026',
  image: '/assets/images/WhatsApp_Image_2026-08-18_at_19.31.00-1787085947624.jpeg',
  imageAlt: 'Formation de professionnalisation créateurs de contenu et influenceurs par Institut Reversal Group',
  reach: '—',
  engagement: '—',
  clicks: '—',
  category: 'Formation'
},
{
  id: 'post-1',
  brand: 'TECNO Mobile',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_161380ca9-1787085813595.png",
  brandLogoAlt: 'TECNO Mobile brand logo',
  title: 'Lancement TECNO Spark 20 Pro',
  content: 'Découvrez le nouveau TECNO Spark 20 Pro — une caméra 108MP, une batterie 5000mAh et un design premium. Disponible maintenant dans tous les points de vente. #TECNOAlgerie #Spark20Pro',
  platform: ['instagram', 'tiktok'],
  status: 'published',
  publishedDate: '15 Aoû 2026',
  image: "https://images.unsplash.com/photo-1727513372253-5e7e260d918d",
  imageAlt: 'TECNO Spark 20 Pro smartphone product launch visual',
  reach: '284K',
  engagement: '8.4%',
  clicks: '12.4K',
  category: 'Tech'
},
{
  id: 'post-2',
  brand: 'Yassir',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1770aff64-1787085812624.png",
  brandLogoAlt: 'Yassir app brand logo',
  title: 'Promo Ramadan — 30% de réduction',
  content: 'Ce Ramadan, profitez de 30% de réduction sur toutes vos courses avec le code RAMADAN30. Valable jusqu\'au 30 mars. Téléchargez Yassir maintenant ! 🌙',
  platform: ['instagram', 'facebook'],
  status: 'scheduled',
  scheduledDate: '20 Aoû 2026',
  image: "https://images.unsplash.com/photo-1672169611243-6f5f4555a5af",
  imageAlt: 'Yassir Ramadan promotion banner with crescent moon design',
  category: 'Services'
},
{
  id: 'post-3',
  brand: 'Samsung Algeria',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_141d57015-1787085812998.png",
  brandLogoAlt: 'Samsung Algeria brand logo',
  title: 'Galaxy S25 — Campagne Lifestyle',
  content: 'Le Galaxy S25 s\'intègre parfaitement dans votre quotidien. Capturez chaque moment avec une qualité professionnelle. #GalaxyS25 #SamsungAlgerie',
  platform: ['instagram', 'tiktok', 'linkedin'],
  status: 'draft',
  category: 'Tech'
},
{
  id: 'post-4',
  brand: 'Ooredoo Algeria',
  brandLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6351580-1787085812373.png",
  brandLogoAlt: 'Ooredoo Algeria telecom brand logo',
  title: 'Forfait 5G — Été 2026',
  content: 'Restez connectés cet été avec notre nouveau forfait 5G illimité. Navigation ultra-rapide, appels illimités, 100GB de data. Souscrivez en ligne dès maintenant.',
  platform: ['instagram', 'facebook', 'tiktok'],
  status: 'published',
  publishedDate: '10 Aoû 2026',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eed684de-1766833761903.png",
  imageAlt: 'Ooredoo 5G summer promotion with network speed visualization',
  reach: '420K',
  engagement: '6.2%',
  clicks: '18.7K',
  category: 'Telecom'
}];


const PLATFORM_CONFIG: Record<PostPlatform, {label: string;color: string;icon: string;}> = {
  instagram: { label: 'Instagram', color: 'bg-pink-500/15 text-pink-400 border-pink-500/30', icon: '📸' },
  tiktok: { label: 'TikTok', color: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30', icon: '🎵' },
  facebook: { label: 'Facebook', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30', icon: '👥' },
  linkedin: { label: 'LinkedIn', color: 'bg-sky-600/15 text-sky-400 border-sky-600/30', icon: '💼' }
};

const STATUS_CONFIG: Record<PostStatus, {label: string;color: string;}> = {
  published: { label: 'Publié', color: 'bg-positive/15 text-positive border-positive/30' },
  draft: { label: 'Brouillon', color: 'bg-muted text-muted-foreground border-border' },
  scheduled: { label: 'Planifié', color: 'bg-warning/15 text-warning border-warning/30' },
  archived: { label: 'Archivé', color: 'bg-muted text-muted-foreground/60 border-border' }
};

const BRANDS = ['Toutes les marques', 'Institut Reversal Group', 'TECNO Mobile', 'Yassir', 'Samsung Algeria', 'Ooredoo Algeria'];
const STATUSES: (PostStatus | 'all')[] = ['all', 'published', 'scheduled', 'draft', 'archived'];
const PLATFORMS: (PostPlatform | 'all')[] = ['all', 'instagram', 'tiktok', 'facebook', 'linkedin'];

function CreatePostModal({ onClose, onSave }: {onClose: () => void;onSave: (post: BrandPost) => void;}) {
  const [form, setForm] = useState({
    brand: '',
    title: '',
    content: '',
    platforms: [] as PostPlatform[],
    status: 'draft' as PostStatus,
    scheduledDate: '',
    category: ''
  });

  const togglePlatform = (p: PostPlatform) => {
    setForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p]
    }));
  };

  const handleSave = () => {
    if (!form.brand || !form.title || !form.content) return;
    const newPost: BrandPost = {
      id: `post-${Date.now()}`,
      brand: form.brand,
      brandLogo: 'https://img.rocket.new/generatedImages/rocket_gen_img_19b3fe9e0-1773215727744.png',
      brandLogoAlt: `${form.brand} brand logo`,
      title: form.title,
      content: form.content,
      platform: form.platforms,
      status: form.status,
      scheduledDate: form.scheduledDate || undefined,
      category: form.category || 'Général'
    };
    onSave(newPost);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="PencilSquareIcon" size={16} className="text-primary" />
            </div>
            <h2 className="text-base font-700 text-foreground">Nouvelle publication</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Marque *</label>
              <input
                value={form.brand}
                onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value }))}
                placeholder="Ex: TECNO Mobile"
                className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
              
            </div>
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Catégorie</label>
              <input
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                placeholder="Ex: Tech, Beauty, Food..."
                className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
              
            </div>
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Titre de la publication *</label>
            <input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Titre accrocheur de votre publication"
              className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
            
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Contenu / Caption *</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              placeholder="Rédigez votre publication ici... Incluez hashtags, mentions, call-to-action."
              rows={5}
              className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors resize-none" />
            
            <p className="text-xs text-muted-foreground mt-1">{form.content.length} caractères</p>
          </div>
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-2 uppercase tracking-wide">Plateformes cibles</label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(PLATFORM_CONFIG) as PostPlatform[]).map((p) =>
              <button
                key={p}
                onClick={() => togglePlatform(p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-600 transition-all ${
                form.platforms.includes(p) ?
                PLATFORM_CONFIG[p].color + 'border-current' : 'bg-muted text-muted-foreground border-border hover:border-primary/40'}`
                }>
                
                  <span>{PLATFORM_CONFIG[p].icon}</span>
                  {PLATFORM_CONFIG[p].label}
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Statut</label>
              <select
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as PostStatus }))}
                className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors">
                
                <option value="draft">Brouillon</option>
                <option value="scheduled">Planifier</option>
                <option value="published">Publier maintenant</option>
              </select>
            </div>
            {form.status === 'scheduled' &&
            <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wide">Date de publication</label>
                <input
                type="datetime-local"
                value={form.scheduledDate}
                onChange={(e) => setForm((p) => ({ ...p, scheduledDate: e.target.value }))}
                className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary transition-colors" />
              
              </div>
            }
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-600 text-muted-foreground hover:bg-muted transition-all">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!form.brand || !form.title || !form.content}
            className="px-5 py-2 rounded-xl text-sm font-700 bg-primary text-primary-foreground hover:bg-primary/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            
            {form.status === 'published' ? 'Publier' : form.status === 'scheduled' ? 'Planifier' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>);

}

function PostCard({ post, onDelete }: {post: BrandPost;onDelete: (id: string) => void;}) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_CONFIG[post.status];

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 transition-all group">
      {post.image &&
      <div className="h-40 overflow-hidden">
          <AppImage
          src={post.image}
          alt={post.imageAlt || post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        
        </div>
      }
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
              <AppImage src={post.brandLogo} alt={post.brandLogoAlt} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-600 text-muted-foreground truncate">{post.brand}</p>
              <p className="text-sm font-700 text-foreground truncate">{post.title}</p>
            </div>
          </div>
          <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-600 border ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
        </div>

        <p className={`text-sm text-muted-foreground leading-relaxed mb-3 ${expanded ? '' : 'line-clamp-2'}`}>
          {post.content}
        </p>
        {post.content.length > 100 &&
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-primary font-600 mb-3 hover:underline">
            {expanded ? 'Voir moins' : 'Voir plus'}
          </button>
        }

        <div className="flex flex-wrap gap-1.5 mb-4">
          {post.platform.map((p) =>
          <span key={p} className={`px-2 py-0.5 rounded-md text-xs font-600 border ${PLATFORM_CONFIG[p].color}`}>
              {PLATFORM_CONFIG[p].icon} {PLATFORM_CONFIG[p].label}
            </span>
          )}
          <span className="px-2 py-0.5 rounded-md text-xs font-600 bg-muted text-muted-foreground border border-border">
            {post.category}
          </span>
        </div>

        {post.status === 'published' && post.reach &&
        <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-muted/50 rounded-xl">
            <div className="text-center">
              <p className="text-sm font-700 text-foreground">{post.reach}</p>
              <p className="text-xs text-muted-foreground">Portée</p>
            </div>
            <div className="text-center border-x border-border">
              <p className="text-sm font-700 text-positive">{post.engagement}</p>
              <p className="text-xs text-muted-foreground">Engagement</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-700 text-foreground">{post.clicks}</p>
              <p className="text-xs text-muted-foreground">Clics</p>
            </div>
          </div>
        }

        {post.status === 'scheduled' && post.scheduledDate &&
        <div className="flex items-center gap-2 mb-4 p-2.5 bg-warning/10 rounded-lg border border-warning/20">
            <Icon name="ClockIcon" size={14} className="text-warning flex-shrink-0" />
            <p className="text-xs text-warning font-600">Planifié le {post.scheduledDate}</p>
          </div>
        }

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {post.publishedDate ? `Publié le ${post.publishedDate}` : post.scheduledDate ? `Planifié` : 'Brouillon'}
          </p>
          <div className="flex items-center gap-1">
            <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
              <Icon name="PencilSquareIcon" size={14} />
            </button>
            <button
              onClick={() => onDelete(post.id)}
              className="p-1.5 rounded-lg hover:bg-negative/10 text-muted-foreground hover:text-negative transition-all">
              
              <Icon name="TrashIcon" size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>);

}

export default function BrandPublicationsPage() {
  const [posts, setPosts] = useState<BrandPost[]>(mockPosts);
  const [showCreate, setShowCreate] = useState(false);
  const [filterBrand, setFilterBrand] = useState('Toutes les marques');
  const [filterStatus, setFilterStatus] = useState<PostStatus | 'all'>('all');
  const [filterPlatform, setFilterPlatform] = useState<PostPlatform | 'all'>('all');
  const [search, setSearch] = useState('');

  const filtered = posts.filter((p) => {
    if (filterBrand !== 'Toutes les marques' && p.brand !== filterBrand) return false;
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (filterPlatform !== 'all' && !p.platform.includes(filterPlatform)) return false;
    if (search && !p.title.toLowerCase().includes(search.toLowerCase()) && !p.brand.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: posts.length,
    published: posts.filter((p) => p.status === 'published').length,
    scheduled: posts.filter((p) => p.status === 'scheduled').length,
    draft: posts.filter((p) => p.status === 'draft').length
  };

  return (
    <AppLayout currentPath="/brand-publications">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-800 text-foreground">Publications Marques</h1>
            <p className="text-sm text-muted-foreground mt-1">Créez et gérez les publications pour vos marques partenaires</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-700 hover:bg-primary/90 transition-all active:scale-95 flex-shrink-0">
            
            <Icon name="PlusIcon" size={16} />
            Nouvelle publication
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
          { label: 'Total', value: stats.total, icon: 'DocumentTextIcon', color: 'text-foreground' },
          { label: 'Publiés', value: stats.published, icon: 'CheckCircleIcon', color: 'text-positive' },
          { label: 'Planifiés', value: stats.scheduled, icon: 'ClockIcon', color: 'text-warning' },
          { label: 'Brouillons', value: stats.draft, icon: 'PencilIcon', color: 'text-muted-foreground' }].
          map((kpi) =>
          <div key={kpi.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={18} className={kpi.color} />
              </div>
              <div>
                <p className={`text-xl font-800 ${kpi.color}`}>{kpi.value}</p>
                <p className="text-xs text-muted-foreground">{kpi.label}</p>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une publication..."
                className="w-full bg-muted border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors" />
              
            </div>
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors">
              
              {BRANDS.map((b) => <option key={b}>{b}</option>)}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as PostStatus | 'all')}
              className="bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors">
              
              <option value="all">Tous les statuts</option>
              {STATUSES.filter((s) => s !== 'all').map((s) =>
              <option key={s} value={s}>{STATUS_CONFIG[s as PostStatus].label}</option>
              )}
            </select>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value as PostPlatform | 'all')}
              className="bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors">
              
              <option value="all">Toutes les plateformes</option>
              {(Object.keys(PLATFORM_CONFIG) as PostPlatform[]).map((p) =>
              <option key={p} value={p}>{PLATFORM_CONFIG[p].label}</option>
              )}
            </select>
          </div>
        </div>

        {/* Posts Grid */}
        {filtered.length === 0 ?
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
            <Icon name="DocumentTextIcon" size={40} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-base font-600 text-foreground mb-1">Aucune publication trouvée</p>
            <p className="text-sm text-muted-foreground">Modifiez vos filtres ou créez une nouvelle publication</p>
          </div> :

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((post) =>
          <PostCard
            key={post.id}
            post={post}
            onDelete={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))} />

          )}
          </div>
        }
      </div>

      {showCreate &&
      <CreatePostModal
        onClose={() => setShowCreate(false)}
        onSave={(post) => setPosts((prev) => [post, ...prev])} />

      }
    </AppLayout>);

}